import { AxiosError } from 'axios';
import { ApiError, extractApiErrorDescriptor } from '@/shared/errors/api-error';
import { isRequestCanceled } from '@/shared/errors/is-request-canceled';
import { UnsupportedMarketplaceRouteError } from '@/repositories/unsupported-marketplace-error';

const DEFAULT_API_ERROR_CODE = 'HttpRequestFailedError';

const readHeaderValue = (headers: unknown, name: string): string | null => {
    if (!headers || typeof headers !== 'object') {
        return null;
    }

    const headersWithGetter = headers as { get?: (headerName: string) => unknown };
    const fromGetter = typeof headersWithGetter.get === 'function' ? headersWithGetter.get(name) : null;
    if (typeof fromGetter === 'string' && fromGetter) {
        return fromGetter;
    }

    const record = headers as Record<string, unknown>;
    const directValue = record[name] ?? record[name.toLowerCase()] ?? record[name.toUpperCase()];
    if (typeof directValue === 'string' && directValue) {
        return directValue;
    }

    if (Array.isArray(directValue) && typeof directValue[0] === 'string' && directValue[0]) {
        return directValue[0];
    }

    return null;
};

const isJsonContentType = (headers: unknown, payload?: Blob | null): boolean => {
    const headerValue = readHeaderValue(headers, 'content-type');
    if (headerValue?.toLowerCase().includes('application/json')) {
        return true;
    }

    return !!payload?.type && payload.type.toLowerCase().includes('application/json');
};

const normalizeResponsePayload = async ({
    payload,
    headers,
}: {
    payload: unknown;
    headers?: unknown;
}): Promise<unknown> => {
    if (payload === undefined || payload === null) {
        return null;
    }

    if (typeof Blob !== 'undefined' && payload instanceof Blob) {
        const text = await payload.text().catch(() => null);
        if (!text) {
            return null;
        }

        if (isJsonContentType(headers, payload)) {
            try {
                return JSON.parse(text) as unknown;
            } catch {
                return text;
            }
        }

        return text;
    }

    return payload;
};

const createFallbackApiError = ({
    message,
    code,
    status,
    category,
    cause,
    requestId,
}: {
    message: string;
    code: string;
    status: number;
    category: string;
    cause?: unknown;
    requestId?: string | null;
}): ApiError =>
    new ApiError({
        status,
        descriptor: {
            code,
            message,
            category,
            requestId: requestId ?? undefined,
            correlationId: requestId ?? undefined,
        },
        cause,
    });

export const createRepositoryApiErrorFromResponse = async ({
    status,
    payload,
    headers,
    fallbackMessage,
    fallbackCode = DEFAULT_API_ERROR_CODE,
    fallbackCategory = 'transport',
    cause,
}: {
    status: number;
    payload: unknown;
    headers?: unknown;
    fallbackMessage: string;
    fallbackCode?: string;
    fallbackCategory?: string;
    cause?: unknown;
}): Promise<ApiError> => {
    const normalizedPayload = await normalizeResponsePayload({
        payload,
        headers,
    });
    const requestId = readHeaderValue(headers, 'x-request-id');
    const descriptor = extractApiErrorDescriptor(normalizedPayload);

    if (!descriptor) {
        return createFallbackApiError({
            message: fallbackMessage,
            code: fallbackCode,
            status,
            category: fallbackCategory,
            cause,
            requestId,
        });
    }

    return new ApiError({
        status,
        descriptor: {
            ...descriptor,
            code: descriptor.code === DEFAULT_API_ERROR_CODE ? fallbackCode : descriptor.code || fallbackCode,
            message: descriptor.message || fallbackMessage,
            category: descriptor.category ?? fallbackCategory,
            requestId: descriptor.requestId ?? requestId ?? undefined,
            correlationId: descriptor.correlationId ?? requestId ?? undefined,
        },
        cause,
    });
};

export const createRepositoryApiErrorFromFetchResponse = async ({
    response,
    fallbackMessage,
    fallbackCode = DEFAULT_API_ERROR_CODE,
    fallbackCategory = 'transport',
    cause,
}: {
    response: Response;
    fallbackMessage: string;
    fallbackCode?: string;
    fallbackCategory?: string;
    cause?: unknown;
}): Promise<ApiError> => {
    const payload = await response
        .clone()
        .blob()
        .catch(() => null);

    return await createRepositoryApiErrorFromResponse({
        status: response.status,
        payload,
        headers: response.headers,
        fallbackMessage,
        fallbackCode,
        fallbackCategory,
        cause,
    });
};

export const normalizeRepositoryError = async ({
    error,
    fallbackMessage,
    fallbackCode = DEFAULT_API_ERROR_CODE,
    fallbackCategory = 'transport',
    defaultStatus = 0,
}: {
    error: unknown;
    fallbackMessage: string;
    fallbackCode?: string;
    fallbackCategory?: string;
    defaultStatus?: number;
}): Promise<unknown> => {
    if (isRequestCanceled(error) || error instanceof ApiError || error instanceof UnsupportedMarketplaceRouteError) {
        return error;
    }

    if (error instanceof AxiosError) {
        if (error.response) {
            return await createRepositoryApiErrorFromResponse({
                status: error.response.status,
                payload: error.response.data,
                headers: error.response.headers,
                fallbackMessage,
                fallbackCode,
                fallbackCategory,
                cause: error,
            });
        }

        return createFallbackApiError({
            message: fallbackMessage,
            code: fallbackCode,
            status: error.status ?? defaultStatus,
            category: 'network',
            cause: error,
        });
    }

    if (error instanceof Error) {
        return createFallbackApiError({
            message: error.message.trim() || fallbackMessage,
            code: fallbackCode,
            status: defaultStatus,
            category: fallbackCategory,
            cause: error,
        });
    }

    return createFallbackApiError({
        message: fallbackMessage,
        code: fallbackCode,
        status: defaultStatus,
        category: fallbackCategory,
        cause: error,
    });
};
